import 'react-phone-number-input/style.css';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button, Col, Modal, Row,
} from 'react-bootstrap';
import _ from 'lodash';
import PhoneInput, { isPossiblePhoneNumber, isValidPhoneNumber } from 'react-phone-number-input';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import * as Yup from 'yup';
import { Field, Form, Formik } from 'formik';
import { useAlert } from 'react-alert';

import {
  GQL_CREATE_DIALOG,
  GQL_GET_ME,
  GQL_GET_SETTING,
  GQL_SEND_METASPACE_REQUEST,
} from '../../graphql/queries';
import { ACTIVE, ERROR } from '../../helpers/colors';
import {
  META_ARTIST,
  META_BRAND,
  META_COLLECTOR,
  META_CREATOR,
  META_DESIGNER,
  META_DEVELOPER,
  META_MARKETER,
  META_PRODUCER,
} from '../../helpers/main';
import { LANG_RU } from '../../locales';
import { ArtField } from '../forms';
import { useTranslation } from '../hooks/useTranslation';
import InfoBox from '../InfoBox';
import ModalCloseButton from '../ModlaCloseButton';

const MetaspaceModal = ({ show, onHide }) => {
  const client = useApolloClient();
  const history = useHistory();
  const { t } = useTranslation();
  const alert = useAlert();

  const [loading, setLoading] = useState(false);
  const [hide, hideContent] = useState(false);
  const [initialValues, setInitialValues] = useState({
    name: '',
    phone: '',
    role: '',
    message: '',
  });

  const { data: dataSetting } = useQuery(GQL_GET_SETTING, {
    variables: { code: 'moderator_id' },
  });

  const data = client.readQuery({ query: GQL_GET_ME });

  const me = _.get(data, 'me', null);

  const companion_id = _.get(dataSetting, 'setting.value', null);

  const [sendRequest] = useMutation(GQL_SEND_METASPACE_REQUEST);

  const [dialogProcess] = useMutation(GQL_CREATE_DIALOG);

  const reset = () => setInitialValues({
    name: '',
    phone: '',
    role: '',
    message: '',
  });

  useEffect(
    () => () => {
      reset();
    },
    [],
  );

  const contactModerator = async () => {
    if (!companion_id) {
      return false;
    }
    try {
      await dialogProcess({
        variables: { companion_id },
        fetchPolicy: 'no-cache',
      }).then((response) => {
        if (_.get(response, 'data.createDialog._id')) {
          history.push({
            pathname: `/account/messages/${_.get(response, 'data.createDialog._id')}`,
            state: {
              message: t('hello_moderator', { ns: 'metaspace' }),
            },
          });
        }
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
    }
    return true;
  };

  const handleUpdate = async (val, { setSubmitting, setErrors }) => {
    try {
      setLoading(true);
      const variables = _.pick(val, ['name', 'phone', 'role', 'message']);
      const response = await sendRequest({ variables });
      const errors = _.get(response, 'errors.[0].extensions', {});
      if (!_.isEmpty(errors)) {
        setSubmitting(false);
        setErrors(errors);
      } else {
        hideContent(true);
        reset();
        alert.show(t('send_success', { ns: 'metaspace' }), { type: 'success' });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
    }
    setLoading(false);
  };

  const validatePhone = (phone) => (phone ? isPossiblePhoneNumber(phone) && isValidPhoneNumber(phone) : true);

  const validationSchema = !_.get(me, '_id', null)
    ? Yup.object({
      name: Yup.string(t('only_text', { ns: 'validate' }))
        .required(t('required', { ns: 'validate' }))
        .nullable(),
      phone: Yup.string(t('only_text', { ns: 'validate' }))
        .required(t('required', { ns: 'validate' }))
        .min(5, t('minimum_length', { ns: 'validate', count: 5 }))
        .max(20, t('maximum_length', { ns: 'validate', count: 20 }))
        .nullable(),
      message: Yup.string(t('only_text', { ns: 'validate' }))
        .required(t('required', { ns: 'validate' }))
        .nullable(),
      role: Yup.string(t('only_text', { ns: 'validate' }))
        .required(t('required_check', { ns: 'validate' }))
        .nullable(),
    })
    : Yup.object({
      message: Yup.string(t('only_text', { ns: 'validate' }))
        .required(t('required', { ns: 'validate' }))
        .nullable(),
      role: Yup.string(t('only_text', { ns: 'validate' }))
        .required(t('required_check', { ns: 'validate' }))
        .nullable(),
    });

  const META_ROLES = [
    { value: META_ARTIST, label: t('artist', { ns: 'metaspace' }) },
    { value: META_CREATOR, label: t('creator', { ns: 'metaspace' }) },
    { value: META_COLLECTOR, label: t('collector', { ns: 'metaspace' }) },
    { value: META_PRODUCER, label: t('producer', { ns: 'metaspace' }) },
    { value: META_DESIGNER, label: t('designer', { ns: 'metaspace' }) },
    { value: META_MARKETER, label: t('marketer', { ns: 'metaspace' }) },
    { value: META_BRAND, label: t('brand', { ns: 'metaspace' }) },
    { value: META_DEVELOPER, label: t('developer', { ns: 'metaspace' }) },
  ];

  return (
    <Modal size="cs" show={show} onHide={onHide} backdrop="static" centered>
      <div className="p-2 px-md-5 py-md-3">
        <ModalHeader>
          <CardName>{t('add', { ns: 'metaspace' })}</CardName>
          <ModalCloseButton onHide={onHide} loading={loading} />
        </ModalHeader>
        {!hide ? (
          <Modal.Body className="p-3">
            <Formik
              initialValues={initialValues}
              onSubmit={handleUpdate}
              validationSchema={validationSchema}
              enableReinitialize
            >
              {({
                isSubmitting, setFieldValue, values, errors,
              }) => (
                <FormStyle>
                  {!_.get(me, '_id', null) && (
                  <>
                    <Area>
                      <H4>{t('name', { ns: 'metaspace' })}</H4>
                      <Field
                        name="name"
                        disabled={loading}
                        className="form-control-lg"
                        placeholder={t('name_placeholder', { ns: 'metaspace' })}
                        column
                        component={ArtField}
                      />
                    </Area>
                    <Area>
                      <H4>{t('phone', { ns: 'metaspace' })}</H4>
                      <PhoneInput
                        international
                        name="phone"
                        required
                        className={`form-control-lg ${
                          _.get(errors, 'phone') || !validatePhone(values.phone) ? 'is-invalid' : ''
                        }`}
                        value={values.phone}
                        placeholder={t('phone_placeholder', { ns: 'metaspace' })}
                        defaultCountry={LANG_RU.toUpperCase()}
                        onChange={(val) => setFieldValue('phone', val)}
                      />
                      {_.get(errors, 'phone') && (<Error>{_.get(errors, 'phone')}</Error>)}
                    </Area>
                  </>
                  )}
                  <Area>
                    <Row>
                      <H4>{t('role', { ns: 'metaspace' })}</H4>
                      {META_ROLES.map((role) => (
                        <Col lg={6} xl={6} md={6} sm={12}>
                          <RadioLabel htmlFor={role.value}>
                            <Field
                              type="radio"
                              name="role"
                              id={role.value}
                              value={role.value}
                              checked={values.role === role.value}
                            />
                            <div className="check" />
                            {role.label}
                          </RadioLabel>
                        </Col>
                      ))}
                      {_.get(errors, 'role') && (<Error>{_.get(errors, 'role')}</Error>)}
                    </Row>
                  </Area>
                  <Area>
                    <H4>{t('message', { ns: 'metaspace' })}</H4>
                    <Field
                      name="message"
                      disabled={loading}
                      className={`form-control-lg w-100 ${_.get(errors, 'message') ? 'is-invalid' : ''}`}
                      placeholder={t('message_placeholder', { ns: 'metaspace' })}
                      column
                      as="textarea"
                    />
                    {_.get(errors, 'message') && (
                      <Error>
                        {_.get(errors, 'message')}
                      </Error>
                    )}
                  </Area>
                  {_.get(me, '_id', null) && (
                    <InfoBox>
                      {t('info', { ns: 'metaspace' })}
                    </InfoBox>
                  )}
                  <Footer>
                    <Agreement>
                      {t('agreement', { ns: 'metaspace' })}
                      <a rel="noreferrer nofollow noopener" target="_blank" href="/privacy-policy">
                        {t('agreement_link', { ns: 'metaspace' })}
                      </a>
                    </Agreement>
                    <Buttons>
                      {companion_id && _.get(me, '_id', null) && (
                        <Button
                          type="button"
                          variant="outline-primary"
                          size="lg"
                          onClick={contactModerator}
                          disabled={isSubmitting || loading}
                        >
                          {t('contact_moderator', { ns: 'action' })}
                        </Button>
                      )}
                      <Button
                        type="submit"
                        variant="primary"
                        size="lg"
                        disabled={isSubmitting || loading}
                      >
                        {isSubmitting ? t('await', { ns: 'action' }) : t('send', { ns: 'action' })}
                      </Button>
                    </Buttons>
                  </Footer>
                </FormStyle>
              )}
            </Formik>
          </Modal.Body>
        ) : (
          <Modal.Body className="p-3">
            {!_.get(me, '_id', null)
              ? t('thanks', { ns: 'metaspace' })
              : t('thanks_user', { ns: 'metaspace' })}
          </Modal.Body>
        )}
      </div>
    </Modal>
  );
};

const ModalHeader = styled.div`
  display: flex;
  padding: 1rem;
  width: 100%;
  justify-content: space-between;
  @media screen and (max-width: 500px) {
    flex-direction: column-reverse;
    justify-content: flex-end;
    align-items: flex-end;
  }
`;

const Error = styled.div`
  margin-top: 1.25rem;
  font-size: .875em;
  color: ${ERROR};
`;

const Area = styled.div`
  margin-bottom: 40px;
  .invalid-feedback {
    color: ${ERROR};
  }
  .is-invalid {
    border-color: ${ERROR};
  }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  @media screen and (max-width: 991px) {
    flex-direction: column-reverse;
  }
`;

const Buttons = styled.div`
  display: flex;
  align-items: center;
  button {
    margin-left: 20px;
  }
  @media screen and (max-width: 991px) {
    flex-direction: column;
    button {
      width: 100%;
      margin-left: 0;
      margin-bottom: 20px;
    }
  }
  @media screen and (max-width: 500px) {
    width: 100%;
  }
`;

const Agreement = styled.div`
  font-weight: 400;
  font-size: 16px;
  line-height: 130%;
  a {
    display: block;
    color: ${ACTIVE};
    font-weight: 400;
    font-size: 16px;
    line-height: 130%;
    &:hover, &:focus {
      text-decoration: underline;
    }
  }
`;

const H4 = styled.h4`
  font-weight: 700;
  font-size: 24px;
  line-height: 120%;
  margin-bottom: 20px;
  @media screen and (max-width: 500px) {
    font-size: 16px;
    line-height: 20px;
  }
`;

export const RadioLabel = styled.label`
  display: inline-flex;
  cursor: pointer;
  align-items: center;
  font-size: 20px;
  margin-bottom: 0;
  .check {
    margin-right: 20px;
  }
  @media screen and (max-width: 1170px) {
    font-size: 80%;
  }
  @media screen and (max-width: 768px) {
    font-size: 90%;
  }
  @media screen and (max-width: 500px) {
    font-size: 80%;
    margin-bottom: 10px;
  }
`;

const FormStyle = styled(Form)`
  .PhoneInputInput {
    border: 0;
  }
  @media screen and (max-width: 768px) {
    .form-control {
      padding: 1rem 19px;
      height: calc(1.5em + 0.75rem + 20px);
    }
  }
`;

const CardName = styled.div`
  font-weight: bold;
  font-size: 40px;
  line-height: 120%;
  margin-bottom: 20px;
  @media screen and (max-width: 1024px) {
    font-size: 32px;
    text-align: center;
  }
  @media screen and (max-width: 768px) {
    font-size: 26px;
    text-align: left;
  }
`;

MetaspaceModal.defaultProps = {
  show: false,
};

MetaspaceModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
};

export default MetaspaceModal;
